import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, X, ChevronDown } from "lucide-react";

// Date-range button with 7 presets + custom range. Emits ISO date strings
// (YYYY-MM-DD) via `onChange({ from, to, label })`. When `label` is null the
// filter is cleared.

const PRESETS = [
  { key: "today",       label: "Today" },
  { key: "yesterday",   label: "Yesterday" },
  { key: "last7",       label: "Last 7 Days" },
  { key: "last30",      label: "Last 30 Days" },
  { key: "this_month",  label: "This Month" },
  { key: "last_month",  label: "Last Month" },
  { key: "custom",      label: "Custom Range" },
];

function isoDay(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function rangeForPreset(key) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  let from = startOfDay(now);
  let to = endOfDay(now);
  if (key === "yesterday") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    from = startOfDay(y); to = endOfDay(y);
  } else if (key === "last7") {
    const s = new Date(now); s.setDate(s.getDate() - 6);
    from = startOfDay(s);
  } else if (key === "last30") {
    const s = new Date(now); s.setDate(s.getDate() - 29);
    from = startOfDay(s);
  } else if (key === "this_month") {
    from = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (key === "last_month") {
    from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  }
  return { from: isoDay(from), to: isoDay(to) };
}

/** onChange({ from, to, label }) | onChange(null) to clear */
export default function DateRangeButton({ value, onChange, testId = "date-range" }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(value?.preset || "");
  const [customFrom, setCustomFrom] = useState(value?.from || "");
  const [customTo, setCustomTo] = useState(value?.to || "");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const label = useMemo(() => {
    if (!value?.label) return "Date Range";
    return value.label;
  }, [value]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return; // wait for user to pick dates
    const { from, to } = rangeForPreset(key);
    const chosen = PRESETS.find((p) => p.key === key);
    onChange?.({ preset: key, from, to, label: chosen?.label || key });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    onChange?.({ preset: "custom", from: customFrom, to: customTo, label: `${customFrom} → ${customTo}` });
    setOpen(false);
  };

  const clear = () => {
    setPreset("");
    setCustomFrom("");
    setCustomTo("");
    onChange?.(null);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative inline-block" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-button`}
        className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border text-sm font-semibold transition-colors ${
          value?.label
            ? "bg-[#0F62FE] text-white border-[#0F62FE] hover:bg-[#0043A8]"
            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
        }`}
      >
        <Calendar className="w-3.5 h-3.5" />
        <span>{label}</span>
        {value?.label && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            data-testid={`${testId}-clear`}
            className="ml-1 -mr-1 rounded-full hover:bg-white/25 p-0.5"
            title="Clear filter"
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && (
        <div
          className="absolute right-0 sm:right-auto sm:left-0 top-full mt-1 z-30 w-64 rounded-lg border border-slate-200 bg-white shadow-xl p-2"
          data-testid={`${testId}-menu`}
        >
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              data-testid={`${testId}-preset-${p.key}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-slate-50 ${
                preset === p.key ? "bg-blue-50 text-[#0F62FE] font-semibold" : "text-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
          {preset === "custom" && (
            <div className="mt-2 pt-2 border-t border-slate-200 space-y-2">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">From</label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid={`${testId}-custom-from`}
                  className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">To</label>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid={`${testId}-custom-to`}
                  className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                />
              </div>
              <button
                onClick={applyCustom}
                disabled={!customFrom || !customTo}
                data-testid={`${testId}-custom-apply`}
                className="w-full h-9 rounded-md bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0043A8] disabled:opacity-40"
              >
                Apply custom range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
